// @givmo/api-client — client configuration surface.
//
// Base URL is `api.givmo.io` from day one (Combined Plan 03 §4, 01/02·A), but
// configurable: an app passes an explicit `baseUrl`, or we read it from the
// environment. We do NOT read `process.env` directly here — SSR (Node/Cloud Run)
// and SPA (Vite `import.meta.env`) expose env differently — so the host supplies
// the resolved value; {@link resolveBaseUrl} only applies the default + hygiene.

import type { RequestIdPrefix } from './correlation';

/** The production default. Apps override per-environment via `baseUrl`. */
export const DEFAULT_BASE_URL = 'https://api.givmo.io';

export interface FirebaseAuthProvider {
  /**
   * Resolve the current Firebase ID token, or `null` when unauthenticated.
   * `forceRefresh` is honored by {@link GivmoClientConfig.forceTokenRefresh} and
   * the pre-download refresh hook.
   */
  getIdToken(forceRefresh?: boolean): Promise<string | null>;
}

export interface FirebaseAppCheckProvider {
  /** Resolve the current App Check token, or `null` if unavailable. */
  getToken(): Promise<string | null>;
  /**
   * Mint a fresh limited-use token for one replay-protected request. Optional so every existing
   * standard-token provider remains source-compatible; a matching limited-use policy without this
   * method fails closed before the request reaches the wire.
   */
  getLimitedUseToken?(): Promise<string | null>;
}

/** The stable request fields an app-level route policy may inspect. */
export interface GivmoRequestDescriptor {
  /** Native Fetch method (`GET`, `POST`, ...). */
  readonly method: string;
  /** Serialized URL pathname only (no origin or query string). */
  readonly path: string;
}

/**
 * App-level policy for routes whose backend enables App Check replay protection.
 *
 * Matching requests use {@link FirebaseAppCheckProvider.getLimitedUseToken} instead of the standard
 * cached provider. A distinct `reauth_required` 401 may invoke the optional handler and replay once;
 * the handler is expected to wait for a user-gesture-anchored reauthentication surface.
 */
export interface LimitedUseAppCheckPolicy {
  readonly matches: (request: GivmoRequestDescriptor) => boolean;
  readonly onReauthenticationRequired?: () => Promise<void>;
}

export interface GivmoClientConfig {
  /** Per-app correlation prefix (`wb`/`sa`/`dw`/`cw`/`pw`/`cn`/`db`). */
  readonly appPrefix: RequestIdPrefix;
  /**
   * API base URL. Defaults to {@link DEFAULT_BASE_URL}. Same-origin gating for
   * the auth/app-check headers keys off this value.
   */
  readonly baseUrl?: string;
  /**
   * Firebase ID-token source. Omit on the unauthenticated donate surface
   * (donate-web has no `firebase_auth` dependency — App Check is its only
   * trust boundary).
   */
  readonly auth?: FirebaseAuthProvider;
  /** Firebase App Check token source. Present on every app (incl. donate). */
  readonly appCheck?: FirebaseAppCheckProvider;
  /**
   * Optional route policy for replay-protected calls. Omit to preserve the standard cached App
   * Check behavior for every request.
   */
  readonly limitedUseAppCheck?: LimitedUseAppCheckPolicy;
  /** Enables the console network logger. Host wires `!isProduction`. Default off. */
  readonly debugLogEnabled?: boolean;
  /**
   * Test seam: a request-id generator override. Production leaves this unset and
   * the `crypto`-backed generator runs.
   */
  readonly requestIdRng?: () => number;
}

/** Apply the default + trailing-slash hygiene (the generated client also trims, belt-and-suspenders). */
export function resolveBaseUrl(baseUrl: string | undefined): string {
  const url = (baseUrl ?? DEFAULT_BASE_URL).trim();
  return url.endsWith('/') ? url.slice(0, -1) : url;
}
